/**
 * ADS-INSTR-2026-003 — did a cashier refused for stock actually SEE the ADS-FIX-2026-003 guidance,
 * and did it lead to an acceptance?
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * WHAT THIS EXISTS TO ANSWER
 *
 * ADS-FIX-2026-003 replaced the `stock_unavailable` toast with `StockShortageModal` (the short
 * product, its stock, the attempted quantity, and a door into the acceptance form). Its 7-day
 * readout could not say whether it worked, because it could not say who had it: the fix shipped as
 * an OTA, `$app_version` does not move on an OTA, and `sale_checkout_blocked` is emitted identically
 * by bundles with and without the dialog. Users who never received the update and users who saw the
 * dialog and ignored it produced the same stream.
 *
 * Three events separate them:
 *
 *   `stock_unavailable_guidance_shown`      the dialog was rendered visible. Its existence is the
 *                                           proof that this device runs the treatment.
 *   `stock_unavailable_receiving_opened`    «Оформить приёмку» was pressed and the acceptance form
 *                                           was opened from the dialog.
 *   `stock_unavailable_receiving_confirmed` that acceptance form was SAVED — the same moment, and
 *                                           the same meaning, as `supply_created`. Closing it,
 *                                           backing out, or a failed save emits nothing.
 *
 * `sale_checkout_blocked` is untouched: its call sites, conditions and payload are exactly as
 * before (`utils/checkoutBlocked.ts`).
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * THE PAYLOAD IS CLOSED — SAME RULE AS `sale_checkout_blocked`
 *
 * Each capture builds its own property bag from fixed enums and ignores everything else the caller
 * passes. The moment these fire is the moment the tempting context is on screen — the product name,
 * its stock, the quantity, the cart — and none of it may leave the device. Allowed, and nothing else:
 *
 *   variant            always `fix003_guidance_v1` (which guidance UI this is)
 *   stage              `cart` | `checkout` (where the refusal happened) | `unknown`
 *   receiving_offered  (shown only) whether the dialog offered the acceptance button — the
 *                      denominator for the open rate; a role without the Supplies permission is
 *                      shown «ask an administrator» and can never open
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * NO ATTEMPT ID — DELIBERATELY
 *
 * The task proposed linking the three events with a random one-attempt `attempt_id`, conditional on
 * confirming that this is allowed and its retention fits the PostHog rules. It could not be
 * confirmed, so it is not sent:
 *
 *   · No written Atlas rule permits an event-level correlation id; the one rule for app telemetry
 *     (`global/analytics/operationalTelemetry.ts`) says "no high-cardinality identifiers".
 *   · "Short-lived" is not achievable where it matters: PostHog keeps product-analytics events for
 *     7 years on this plan and has no per-property expiry, so the id would live as long as the event.
 *
 * And it is not needed. Every capture already carries the person (`distinct_id`) and PostHog's own
 * rotating `$session_id`, and the three events are ordered in time on one device: `opened` belongs to
 * the latest `shown` before it, and `confirmed` is emitted only by an acceptance form that was opened
 * from the dialog (the `stockGuidance` route marker below), so it cannot be confused with an ordinary
 * acceptance. The sequence queries are in `docs/technical/analytics-stock-guidance.md`.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * ANALYTICS IS NEVER LOAD-BEARING (ATL-901)
 *
 * A missing, partial or throwing PostHog client is tolerated and nothing propagates: these fire
 * while a sale is being refused and while an acceptance is being saved.
 */

import { CHECKOUT_BLOCKED_STAGES } from './checkoutBlocked';

export const STOCK_GUIDANCE_EVENTS = Object.freeze({
  SHOWN: 'stock_unavailable_guidance_shown',
  RECEIVING_OPENED: 'stock_unavailable_receiving_opened',
  RECEIVING_CONFIRMED: 'stock_unavailable_receiving_confirmed',
});

export type StockGuidanceEvent = (typeof STOCK_GUIDANCE_EVENTS)[keyof typeof STOCK_GUIDANCE_EVENTS];

/** The guidance UI these events describe. A redesigned dialog gets a new value, never a reuse. */
export const STOCK_GUIDANCE_VARIANT = 'fix003_guidance_v1';

export const STOCK_GUIDANCE_STAGES = Object.freeze({
  CART: CHECKOUT_BLOCKED_STAGES.CART,
  CHECKOUT: CHECKOUT_BLOCKED_STAGES.CHECKOUT,
  /** The stage was not recorded. Not guessed: a guess would hide the defect that lost it. */
  UNKNOWN: 'unknown',
});

export type StockGuidanceStage = (typeof STOCK_GUIDANCE_STAGES)[keyof typeof STOCK_GUIDANCE_STAGES];

const STAGE_VALUES: readonly string[] = Object.values(STOCK_GUIDANCE_STAGES);

/**
 * Every property any of the three events may carry. The privacy test asserts each captured payload
 * against this list, so a new key is a deliberate edit here rather than a line at a call site.
 */
export const STOCK_GUIDANCE_PROPERTY_KEYS = Object.freeze({
  [STOCK_GUIDANCE_EVENTS.SHOWN]: Object.freeze(['variant', 'stage', 'receiving_offered']),
  [STOCK_GUIDANCE_EVENTS.RECEIVING_OPENED]: Object.freeze(['variant', 'stage']),
  [STOCK_GUIDANCE_EVENTS.RECEIVING_CONFIRMED]: Object.freeze(['variant', 'stage']),
});

/** A `type`, not an `interface`, so it is assignable to `capture`'s `Record` parameter. */
export type StockGuidanceProperties = {
  variant: typeof STOCK_GUIDANCE_VARIANT
  stage: StockGuidanceStage
  receiving_offered?: boolean
};

export interface StockGuidanceOutcome {
  captured: boolean
  event: StockGuidanceEvent
  properties: StockGuidanceProperties
}

/** The narrow slice of the PostHog client used here. Everything about it is optional. */
export interface StockGuidancePostHog {
  capture?: (event: string, properties?: Record<string, unknown>) => unknown
}

/**
 * What New Sale hands the acceptance form so that the form knows it was opened from the guidance.
 * Fixed values only — the form already receives the product through `preselectedItems`, and none of
 * that reaches an event.
 */
export type StockGuidanceRouteMarker = {
  variant: typeof STOCK_GUIDANCE_VARIANT
  stage: StockGuidanceStage
};

export function normalizeStockGuidanceStage(stage: unknown): StockGuidanceStage {
  return typeof stage === 'string' && STAGE_VALUES.includes(stage)
    ? (stage as StockGuidanceStage)
    : STOCK_GUIDANCE_STAGES.UNKNOWN;
}

function send(
  postHog: StockGuidancePostHog | null | undefined,
  event: StockGuidanceEvent,
  properties: StockGuidanceProperties,
): StockGuidanceOutcome {
  try {
    if (typeof postHog?.capture !== 'function') return { captured: false, event, properties };
    postHog.capture(event, properties);
    return { captured: true, event, properties };
  } catch (error) {
    console.warn(`[analytics] ${event} could not be captured; the app continues`, error);
    return { captured: false, event, properties };
  }
}

/** The dialog was rendered visible. Call once per display (StockShortageModal's `onShown`). */
export function captureStockGuidanceShown(
  postHog: StockGuidancePostHog | null | undefined,
  options?: { stage?: unknown, receivingOffered?: unknown },
): StockGuidanceOutcome {
  return send(postHog, STOCK_GUIDANCE_EVENTS.SHOWN, {
    variant: STOCK_GUIDANCE_VARIANT,
    stage: normalizeStockGuidanceStage(options?.stage),
    receiving_offered: options?.receivingOffered === true,
  });
}

/** The acceptance form is being opened from the dialog. */
export function captureStockGuidanceReceivingOpened(
  postHog: StockGuidancePostHog | null | undefined,
  options?: { stage?: unknown },
): StockGuidanceOutcome {
  return send(postHog, STOCK_GUIDANCE_EVENTS.RECEIVING_OPENED, {
    variant: STOCK_GUIDANCE_VARIANT,
    stage: normalizeStockGuidanceStage(options?.stage),
  });
}

/** The route marker New Sale passes to the acceptance form it opens from the dialog. */
export function stockGuidanceRouteMarker(stage: unknown): StockGuidanceRouteMarker {
  return { variant: STOCK_GUIDANCE_VARIANT, stage: normalizeStockGuidanceStage(stage) };
}

/**
 * An acceptance opened from the dialog was saved.
 *
 * Emits only for a marker this module minted: an acceptance form opened any other way carries no
 * marker and must stay out of this event, or every ordinary acceptance would count as a conversion.
 * Returns null when it did not emit.
 */
export function captureStockGuidanceReceivingConfirmed(
  postHog: StockGuidancePostHog | null | undefined,
  marker: unknown,
): StockGuidanceOutcome | null {
  if (
    typeof marker !== 'object' || marker === null
    || (marker as { variant?: unknown }).variant !== STOCK_GUIDANCE_VARIANT
  ) {
    return null;
  }
  return send(postHog, STOCK_GUIDANCE_EVENTS.RECEIVING_CONFIRMED, {
    variant: STOCK_GUIDANCE_VARIANT,
    stage: normalizeStockGuidanceStage((marker as { stage?: unknown }).stage),
  });
}
